<script setup lang="ts">
  import TopBar from '@/components/TopBar.vue'
  import { useProjectValidation } from '@/composables/validation/use-project-validation'
  import { useProjectStore } from '@/store/project.store'
  import { useTemplateStore } from '@/store/template.store'
  import { createEmptyProject, type Project } from '@/types/nest/projects'
  import {
    ConfirmationDialog,
    FormFieldContainer,
    FormInput,
    isUuid,
    Table,
    UiButton,
    useToastStore,
    type Template,
  } from '@logistiek-zonder-papier/lzp-library'
  import { computed, onMounted, ref } from 'vue'
  import { useRoute, useRouter } from 'vue-router'

  const projectStore = useProjectStore()
  const toastStore = useToastStore()
  const templateStore = useTemplateStore()
  const route = useRoute()
  const router = useRouter()

  const projectCopy = ref<Project>(createEmptyProject())
  const templates = ref<Template[]>([])
  const showDeleteModal = ref(false)
  const hasNameChanges = computed(() => {
    if (!projectStore.selectedProject || !projectCopy.value) {
      return false
    }
    return projectStore.selectedProject.name !== projectCopy.value.name
  })

  onMounted(async () => {
    const projectId = route.params['uuid']
    if (!isUuid(projectId)) {
      throw new Error('Project ID is invalid')
    }

    await projectStore.fetchProject(projectId)
    templates.value = await templateStore.fetchByProject(projectId)

    if (projectStore.selectedProject) {
      projectCopy.value = { ...projectStore.selectedProject }
    }
  })

  const { validate, visibleErrors, isSubmittedAndInvalid } = useProjectValidation(
    computed(() => projectStore.selectedProject)
  )

  async function updateProjectName() {
    if (!projectStore.selectedProject) return
    if (!validate()) return
    try {
      await projectStore.updateProject(projectStore.selectedProject)
      projectCopy.value = { ...projectStore.selectedProject }

      toastStore.success('Project data successfully updated')
    } catch (error) {
      console.error('Error updating project name:', error)
    }
  }

  async function deleteProject() {
    await projectStore.deleteProject(`${route.params['uuid']}`)
    router.back()
  }
</script>

<template>
  <TopBar />
  <div class="shipment-list-container">
    <div v-if="projectStore.selectedProject" class="project-container">
      <FormFieldContainer label="Name">
        <template #action-button>
          <UiButton variant="negative" @click="showDeleteModal = true" icon="delete" />
        </template>
        <FormInput v-model="projectStore.selectedProject.name" :error="visibleErrors.name" />
      </FormFieldContainer>

      <div class="project-button">
        <UiButton
          label="Opslaan"
          variant="prominent"
          @click="updateProjectName"
          :disabled="!hasNameChanges || isSubmittedAndInvalid"
        />
      </div>
    </div>

    <div>
      <Table
        :rows="templates"
        :columns="[
          { key: 'id', label: 'ID' },
          {
            key: 'name',
            label: 'Naam',
            searchable: true,
          },
          {
            key: 'number',
            label: 'Template Nummer',
            searchable: true,
          },
          { key: 'fromAddress', label: 'Herkomst', searchable: true },
          { key: 'toAddress', label: 'Bestemming', searchable: true },
          { key: 'action', label: 'Acties' },
        ]"
        :searchable="true"
        :sortable="true"
        :configurable="true"
      >
        <template #cell-fromAddress="{ row }">{{ row.fromAddress.street2 }}</template>
        <template #cell-toAddress="{ row }">{{ row.toAddress.street2 }}</template>
        <template #cell-number="{ row }">{{
          row.bgb?.groupId ?? row.ecmr?.domainIdentifier ?? 'N/A'
        }}</template>
        <template #cell-action="{ row }">
          <UiButton
            label="Aanpassen"
            variant="prominent"
            @click="router.push({ name: 'template-edit', params: { templateId: row.id } })"
          />
        </template>
        <template #table-top-actions>
          <UiButton
            label="Nieuwe template"
            variant="prominent"
            icon="plus"
            @click="
              router.push({
                name: 'template-new-project',
                params: { projectId: projectStore.selectedProject?.id },
              })
            "
          />
        </template>
      </Table>
    </div>
  </div>

  <ConfirmationDialog
    :title="`Are you sure you want to remove: ${projectCopy?.name}?`"
    body="This action cannot be undone."
    confirmButtonText="Remove"
    confirmButtonColor="negative"
    v-model:show="showDeleteModal"
    :confirm="() => deleteProject()"
  />
</template>

<style scoped lang="css">
  .shipment-list-container {
    padding: var(--spacing-m);
    display: flex;
    flex-direction: column;
    gap: var(--spacing-m);
  }

  .project-button {
    justify-items: end;
  }
</style>
